import CustomStore from "devextreme/data/custom_store";
import DataSource from "devextreme/data/data_source";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    deleteDefaultFile,
    deleteFile, downloadDefaultFile, downloadFileWithName, getDefaultStepFiles, getFileInfo,
    getFiles, getRequestFiles, getResponseFiles, getStepFiles
} from "../../../../../api/v2/FilesApi";
import { FileBlob } from "../../../../../api/v2/ApiUtils";
import { CommentType } from "../../../../../dtos/CommentType";
import { FileDto } from "../../../../../dtos/FileDto";
import { Modules } from "../../../../../dtos/Modules";
import { StepDto } from "../../../../../dtos/StepDto";
import { PermissionType } from "../../../../../dtos/PermissionType";
import { StepStatus } from "../../../../../dtos/StepStatus";
import { useAppContext } from "../../../AppContext";
import { downloadFileByLink } from "../../../fileDownload";
import { sharePointFolderUrl } from "../../sharePointUrl";
import { configuredDocuments, splitConfiguredFiles } from "../../stepFiles";
import { checkStepPermission, canViewResponseContent, getEffectiveStepType, takesConfiguredDocuments } from "../../../Utils";
import { useTaskListContext } from "../../context/TaskListContext";
import { ITaskFilesProps } from "../components/Files";
import { ISubTaskFilesViewProps } from "../components/SubTaskFilesView";
import { confirm } from "devextreme/ui/dialog";

// A slot row for the Submission file list: the configured slot plus its matched file (if any).
export type SlotRow = {
    label: string;
    rename: string | null;
    allowedExtensions: string[] | null;
    file: FileDto | null;
};

export function useTaskFilesController({
    grant, step, fileType = CommentType.None, canDelete, canView = true, showHeader = true, headerText = "Files", canDownloadAll = true, xsrfToken,
}: ITaskFilesProps): ISubTaskFilesViewProps {
    const app = useAppContext();
    const taskListContext = useTaskListContext();

    const stepIdRef = useRef(step.id);
    const fileTypeRef = useRef(fileType);
    const appRef = useRef(app);

    // Live ref to the context so the dataSource store reads the latest methods/state
    // without listing the whole context as a memo dep. Depending on the full
    // taskListContext recreated the store on every tasklist state change, remounting
    // the Files grid and flickering its header (#941).
    const ctxRef = useRef(taskListContext);
    ctxRef.current = taskListContext;

    const [finalCanDownloadAll, setFinalCanDownloadAll] = useState(canDownloadAll);
    // Raw loaded files, used to join slots <-> files for the Submission slot list.
    const [stepFiles, setStepFiles] = useState<FileDto[]>([]);
    // Slot upload/replace dialog (Submission slot mode).
    const [slotDialog, setSlotDialog] = useState<{ open: boolean; slot: SlotRow | null }>({ open: false, slot: null });

    useEffect(() => { stepIdRef.current = step.id; }, [step.id]);
    useEffect(() => { fileTypeRef.current = fileType; }, [fileType]);
    useEffect(() => { appRef.current = app; }, [app]);

    const tasklistMode = taskListContext.tasklistMode;

    const dataSource = useMemo(() => {
        return new DataSource<FileDto, string>({
            paginate: false,
            requireTotalCount: false,
            store: new CustomStore<FileDto, string>({
                key: (tasklistMode !== "templates") ? "id" : "name",
                load: async () => {
                    const ctx = ctxRef.current;
                    const id = stepIdRef.current;
                    const ft = fileTypeRef.current;

                    // Don't load files if step doesn't have an ID yet (new subtask not saved)
                    if (!id) {
                        return { data: [], totalCount: 0 };
                    }

                    let selectedStepFiles = ctx.getSelectedStepFiles(ft);

                    if (selectedStepFiles == null && ctx.tasklistMode == "templates") {
                        // In templates mode, read from step's requestComment.files when cache is empty
                        const currentStep = ctx.getSelectedStep();
                        selectedStepFiles = currentStep?.requestComment?.files ?? [];
                    }

                    if (selectedStepFiles != null) {
                        setFinalCanDownloadAll(selectedStepFiles.length > 1);
                        setStepFiles(selectedStepFiles);
                        return { data: selectedStepFiles, totalCount: selectedStepFiles.length };
                    }

                    let res: Awaited<ReturnType<typeof getFiles>> = null;

                    if (ft == null || ft == CommentType.None)
                        res = await getFiles(id);
                    else if (ft === CommentType.Request)
                        res = await getRequestFiles(id);
                    else
                        res = await getResponseFiles(id);

                    if (res?.error) appRef.current.errorToast(res.error.message);

                    const data = (res?.data ?? []) as FileDto[];

                    ctx.setSelectedStepFiles(ft, data, id);

                    setFinalCanDownloadAll(data.length > 1);
                    setStepFiles(data);

                    return { data, totalCount: data.length };
                },
                byKey: async (key) => {
                    const result = await getFileInfo(key);
                    if (result?.error) appRef.current.errorToast(result.error.message);
                    return result?.data as FileDto;
                },
                remove: async (key) => {
                    const ctx = ctxRef.current;
                    const updatedstep = ctx.getSelectedStep();
                    const result = (ctx.tasklistMode != "templates") ? await deleteFile(key) : await deleteDefaultFile(updatedstep?.defaultStep.id as number, key);

                    if (result?.error) {
                        appRef.current.errorToast(result.error.message);
                        // Throw error to prevent DataSource from removing the item
                        throw new Error(result.error.message);
                    }

                    // Only remove from context if delete succeeded
                    ctx.removeSelectedStepFile(CommentType.Request, key);
                    ctx.removeSelectedStepFile(CommentType.Response, key);
                    ctx.removeSelectedStepFile(CommentType.None, key);
                    await ctx.refreshSteps(updatedstep as StepDto);
                },
            }),
        });
    }, [tasklistMode]);

    // Track file count to trigger reload when files are added/removed in templates mode,
    // where uploads only mutate the in-memory cache. In grant mode the upload flow calls
    // refreshFiles() (which clears the cache and reloads), so also reloading on fileCount
    // here fired a second reload against the stale cache — repainting the old list before
    // the real reload showed the new file (#941 double load / flicker).
    const fileCount = step.requestComment?.files?.length ?? 0;
    const fileCountReloadKey = tasklistMode === "templates" ? fileCount : 0;

    useEffect(() => {
        dataSource.reload();
    }, [dataSource, step.id, fileType, fileCountReloadKey]);

    useEffect(() => {
        if (taskListContext.tasklistMode !== "templates") return;
        if (!step) return;

        // Only initialize cache from step files if no cache exists yet
        // This preserves uploaded files when navigating back to a subtask
        if (taskListContext.hasStepFilesCache(fileType, step.id)) return;

        const request = step.requestComment?.files ?? [];

        // Use the actual fileType prop instead of hardcoding CommentType.Request
        // This ensures None workflow type files are cached correctly
        if (request.length) taskListContext.setSelectedStepFiles(fileType, request, step.id);
    }, [taskListContext.tasklistMode, step?.id, fileType]);

    // Registered here rather than from the grid's onInitialized: the document-slot list renders
    // its own grid over the slot rows, so the file grid never mounts and a handler tied to it was
    // never registered. An upload or delete then cleared the cache with nothing set to reload it.
    // The context never unregisters, so register once and read the current data source through
    // a ref.
    const dataSourceRef = useRef(dataSource);
    dataSourceRef.current = dataSource;
    const registeredRefreshOnce = useRef(false);
    useEffect(() => {
        if (registeredRefreshOnce.current) return;
        registeredRefreshOnce.current = true;
        taskListContext.registerForRefreshFiles(() => { dataSourceRef.current.reload(); });
    }, [taskListContext]);

    const download = useCallback(async (fileId: string, fileName: string) => {
        app.showLoadPanel();
        const selectedStep = taskListContext.getSelectedStep();
        const isTemplates = taskListContext.tasklistMode === "templates";

        // Grant files open through a signed link so the browser streams them itself. Template
        // attachments are on the app server and keep the blob path.
        if (!isTemplates && app.configuration?.streamingFileTransfer) {
            const error = await downloadFileByLink(fileId, fileName);
            app.hideLoadPanel();
            if (error) app.errorToast(error);
            return;
        }
        // Runtime downloads use the server's presentation name (FY/step prefix for Submission,
        // plain stored name otherwise); fall back to the stored name if the header is missing.
        const result = isTemplates
            ? await downloadDefaultFile(selectedStep?.defaultStep.id as number, fileName)
            : await downloadFileWithName(fileId);
        if (result?.error) {
            app.errorToast(result.error.message);
        } else {
            const blob = isTemplates ? (result?.data as Blob) : (result?.data as FileBlob)?.blob;
            const downloadName = isTemplates ? fileName : ((result?.data as FileBlob)?.fileName || fileName);
            if (blob) {
                const link = document.createElement("a");
                const url = URL.createObjectURL(blob);
                window.open(url, "_blank");
                link.href = url; link.download = downloadName;
                document.body.appendChild(link); link.click();
                document.body.removeChild(link);
                setTimeout(() => URL.revokeObjectURL(url), 10000);
            } else {
                app.errorToast(`Failed to download: ${fileName}`);
            }
        }
        app.hideLoadPanel();
    }, [app]);

    const onNameClick = useCallback(async (id: string, name: string) => {
        await download(id, name);
    }, [download]);

    // Removing a file is the same right as putting one there, so every caller passes canDelete -
    // the tasklist from its upload permission, the templates editor as always allowed. The status
    // and uploader-role rules that used to sit here were unreachable behind that and are gone: a
    // file's status rule now lives in one place, the Upload permission its caller reads.
    const allowDeleting = useCallback((): boolean => canDelete ?? false, [canDelete]);

    const downloadAll = useCallback(async () => {
        const phase = taskListContext.getSelectedPhase();
        const moduleType = phase?.defaultPhase?.moduleTypeId ?? Modules.PA;
        const result = (taskListContext.tasklistMode !== "templates") ? await getStepFiles(step.id, moduleType) : await getDefaultStepFiles(step.id, moduleType);
        if (result?.error) app.errorToast(result.error.message);
        else {
            const fileBlob = result?.data;
            if (fileBlob) {
                const link = document.createElement("a");
                const url = URL.createObjectURL(fileBlob.blob);
                link.href = url; link.download = fileBlob.fileName;
                document.body.appendChild(link); link.click();
                document.body.removeChild(link); URL.revokeObjectURL(url);
            } else {
                app.errorToast("Failed to download");
            }
        }
    }, [app, step.id, taskListContext]);

    const downloadButtonOptions = useMemo(() => ({
        icon: "download",
        text: "Download",
        onClick: downloadAll,
    }), [downloadAll]);

    const visible = useMemo(() => {
        // If canView is explicitly provided as prop, use it
        if (canView !== undefined) return canView;

        if (step.status === StepStatus.NOT_STARTED || step.status === StepStatus.INACTIVE) {
            return false;
        }

        // For response files, must be at GRANTEE_PENDING or later (data availability)
        if (fileType === CommentType.Response && !canViewResponseContent(step.status)) {
            return false;
        }

        // Check View permission from config
        const selectedPhase = taskListContext.getSelectedPhase();
        if (!checkStepPermission(step, app, PermissionType.View, true, selectedPhase, grant?.id)) {
            return false;
        }

        // Show if user can upload (can interact with files)
        return checkStepPermission(step, app, PermissionType.Upload, true, selectedPhase, grant?.id);
    }, [canView, step.status, app, step, taskListContext, fileType, grant]);

    const noDataText =
        fileType === CommentType.Request
            ? "No Request files"
            : fileType === CommentType.Response
                ? "No Response files"
                : "No files";

    // Template attachments have no upload date, size or owner to show.
    const showFileInfo = tasklistMode !== "templates";

    // Document rows: a subtask with configured documents renders one row per document (filled or
    // empty) with an in-row upload/replace dialog, instead of free-form uploads. Keyed to the
    // subtask being single-file-area rather than to the task being a Submission, so the rows appear
    // wherever the templates admin was allowed to configure them.
    const settings = step.defaultStep?.settings;
    const restrictions = configuredDocuments(settings);
    const phaseForStep = taskListContext.getSelectedPhase();
    const documentsAllowed = !!phaseForStep?.defaultPhase && !!step.defaultStep
        && takesConfiguredDocuments(getEffectiveStepType(phaseForStep.defaultPhase, step.defaultStep));
    const slotMode = documentsAllowed && tasklistMode !== "templates" && !!restrictions && restrictions.length > 0;
    // canDelete here reflects upload permission (passed as !uploadDisabled() by the caller).
    const canModifySlots = !!canDelete;

    const stripExt = (n: string) => n.replace(/\.[^/.]+$/, "");

    const slots: SlotRow[] = useMemo(() => {
        if (!slotMode || !restrictions) return [];
        return restrictions.map(r => ({
            label: r.label,
            rename: r.rename ?? null,
            allowedExtensions: r.allowedExtensions ?? null,
            // A slot is filled when a file's stored name (minus extension) equals its rename.
            file: r.rename ? (stepFiles.find(f => stripExt(f.name) === r.rename) ?? null) : null,
        }));
    }, [slotMode, restrictions, stepFiles]);

    // Files sitting on the step that no configured document claims - what is left behind when a
    // document is removed from the template after someone uploaded for it. They are still in
    // SharePoint, so the subtask says so rather than quietly hiding them. The same split keeps them
    // out of the file count on the subtask list.
    //
    // Super users only (ticket 1050): it is a housekeeping problem for whoever maintains the
    // templates, and there is nothing a grantee or a PO could do about it.
    const orphanFiles = useMemo(
        () => (slotMode && app.UserIsSuper ? splitConfiguredFiles(stepFiles, settings).orphaned : []),
        [slotMode, app.UserIsSuper, settings, stepFiles]
    );

    // Where those files actually sit. The step records its SharePoint path on first upload, and the
    // library base is served with the app configuration.
    const orphanFolderUrl = useMemo(
        () => sharePointFolderUrl(app.configuration?.spFolderBaseUrl, step.spPath),
        [app.configuration?.spFolderBaseUrl, step.spPath]
    );

    const refreshAfterFileChange = useCallback(async () => {
        const ctx = ctxRef.current;
        const current = ctx.getSelectedStep();
        await ctx.refreshSteps(current as StepDto);
        ctx.refreshFiles();
    }, []);

    // Open the slot upload dialog. No confirm/pre-delete: for a filled slot the dialog shows a
    // "will replace" note, and the existing file is removed only once the user actually picks a
    // replacement (see deleteFileForReplace, run as the uploader's beforeUpload step).
    const onSlotAction = useCallback((slot: SlotRow) => {
        setSlotDialog({ open: true, slot });
    }, []);

    // Delete the existing slot file right before the replacement uploads. The backend rejects a
    // duplicate canonical name, so the old file must be gone first. No refresh here - the upload's
    // own onUploaded refresh covers it. Throws on failure so the uploader aborts the upload.
    const deleteFileForReplace = useCallback(async (fileId: string) => {
        const res = await deleteFile(fileId);
        if (res?.error) {
            app.errorToast(res.error.message);
            throw new Error(res.error.message);
        }
    }, [app]);

    const closeSlotDialog = useCallback(() => setSlotDialog({ open: false, slot: null }), []);

    const onSlotDelete = useCallback(async (slot: SlotRow) => {
        if (!slot.file) return;
        const ok = await confirm(`Delete the file in "${slot.label}"?`, "Delete file");
        if (!ok) return;
        const res = await deleteFile(slot.file.id);
        if (res?.error) { app.errorToast(res.error.message); return; }
        await refreshAfterFileChange();
    }, [app, refreshAfterFileChange]);

    return {
        dataSource,
        onNameClick,
        allowDeleting,
        downloadButtonOptions,
        visible,
        showHeader,
        headerText,
        canDownloadAll: finalCanDownloadAll,
        noDataText,
        showFileInfo,

        // Submission slot list (in-row upload/replace dialog)
        slotMode,
        slots,
        orphanFiles,
        orphanFolderUrl,
        canModifySlots,
        slotDialog,
        onSlotAction,
        onSlotDelete,
        deleteFileForReplace,
        closeSlotDialog,
        grant,
        step,
        xsrfToken,
    };
}
